import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { taskService } from '../services/tasks';
import { telegramService } from '../services/telegram';
import { useTelegramOverlayBack } from '../hooks/useTelegramOverlayBack';

function CreateTask({ showToast }) {
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pendingTaskId, setPendingTaskId] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);

  useTelegramOverlayBack(
    'createTask',
    telegramService.isTelegramWebApp,
    () => telegramService.close(),
    20000
  );

  // Load pending task from URL parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const taskId = urlParams.get('taskId');
    const voiceMode = urlParams.get('voice');
    
    if (taskId) {
      loadPendingTask(taskId);
    } else if (voiceMode === 'true') {
      // Auto-start voice recording when voice=true parameter is present
      handleVoiceRecord();
    }

  }, []);

  const loadPendingTask = async (taskId) => {
    try {
      setLoading(true);
      const response = await taskService.getPendingTask(taskId);
      
      // Convert task to preview format
      const taskPreview = {
        taskName: response.text,
        tags: response.tags || [],
        contacts: response.contacts || [],
        places: response.places || [],
        checklist: response.checklist?.map(c => c.text) || [],
        priority: response.priority,
        model: 'loaded-from-pending'
      };
      
      setPreview(taskPreview);
      setPendingTaskId(taskId);
      showToast('📋 Задача загружена из Telegram', 'success');
    } catch (error) {
      console.error('Error loading pending task:', error);
      showToast('❌ Не удалось загрузить задачу', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyze = async () => {
    if (!text.trim()) return;

    setLoading(true);
    try {
      const result = await taskService.createTaskPreview(text);
      setPreview(result);
    } catch (error) {
      showToast('❌ Ошибка анализа', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleVoiceRecord = async () => {
    try {
      setLoading(true);
      showToast('🎤 Начинаю запись...', 'info');
      
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      const audioChunks = [];
      
      mediaRecorder.ondataavailable = (event) => {
        audioChunks.push(event.data);
      };
      
      mediaRecorder.onstop = async () => {
        try {
          const audioBlob = new Blob(audioChunks, { type: 'audio/ogg' });
          showToast('🔄 Обрабатываю голос...', 'info');
          
          // Transcribe audio
          const transcription = await taskService.transcribeAudio(audioBlob);
          setText(transcription.text);
          
          // Auto-analyze the transcribed text and create pending task
          if (transcription.text.trim()) {
            showToast('🤖 Анализирую задачу...', 'info');
            const result = await taskService.createTaskPreview(transcription.text);
            setPreview(result);
            
            showToast('✅ Задача создана! Переходим к редактированию...', 'success');
            
            // Redirect to edit mode of the created task (pending tasks auto-open in edit mode)
            navigate(`/task/${result.id}`);
          }
        } catch (error) {
          console.error('Error processing voice:', error);
          showToast('❌ Ошибка обработки голоса', 'error');
        } finally {
          setLoading(false);
        }
      };
      
      // Start recording
      mediaRecorder.start();
      showToast('🎤 Записываю... Нажмите для остановки', 'info');
      
      // Stop recording after 10 seconds or when user clicks
      setTimeout(() => {
        if (mediaRecorder.state === 'recording') {
          mediaRecorder.stop();
          stream.getTracks().forEach(track => track.stop());
        }
      }, 10000);
      
    } catch (error) {
      console.error('Error starting voice recording:', error);
      showToast('❌ Ошибка доступа к микрофону', 'error');
      setLoading(false);
    }
  };

  // New microphone functions for press-and-hold recording
  const startRecording = async () => {
    try {
      showToast('🎤 Начинаю запись...', 'info');
      
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const audioChunks = [];
      
      recorder.ondataavailable = (event) => {
        audioChunks.push(event.data);
      };
      
      recorder.onstop = async () => {
        try {
          const audioBlob = new Blob(audioChunks, { type: 'audio/ogg' });
          showToast('🔄 Обрабатываю голос...', 'info');
          
          const transcription = await taskService.transcribeAudio(audioBlob);
          setText(transcription.text);
          
          // Auto-analyze the transcribed text and create pending task
          if (transcription.text.trim()) {
            showToast('🤖 Анализирую задачу...', 'info');
            const result = await taskService.createTaskPreview(transcription.text);
            setPreview(result);
            
            showToast('✅ Задача создана! Переходим к редактированию...', 'success');
            
            // Redirect to edit mode of the created task (pending tasks auto-open in edit mode)
            navigate(`/task/${result.id}`);
          }
        } catch (error) {
          console.error('Error processing voice:', error);
          showToast('❌ Ошибка обработки голоса', 'error');
        } finally {
          setIsRecording(false);
          setMediaRecorder(null);
        }
      };
      
      setMediaRecorder(recorder);
      setIsRecording(true);
      recorder.start();
      showToast('🎤 Записываю... Отпустите для остановки', 'info');
      
    } catch (error) {
      console.error('Error starting voice recording:', error);
      showToast('❌ Ошибка доступа к микрофону', 'error');
      setIsRecording(false);
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      // Stop all tracks to release microphone
      if (mediaRecorder.stream) {
        mediaRecorder.stream.getTracks().forEach(track => track.stop());
      }
    }
  };

  const handleCreate = async () => {
    if (!preview) return;

    setLoading(true);
    try {
      if (pendingTaskId) {
        // Task already created, just confirm it
        await taskService.updateTaskFull(pendingTaskId, { ...preview, status: 'todo' });
        showToast('✅ Задача подтверждена!', 'success');
      } else {
        // Create new task from preview (now creates pending task directly)
        const createdTask = await taskService.createTaskPreview(preview.text);
        showToast('✅ Задача создана!', 'success');
        // Redirect to edit mode for pending task
        navigate(`/task/${createdTask.id}`);
        return;
      }
      navigate('/');
    } catch (error) {
      showToast('❌ Ошибка создания', 'error');
      setLoading(false);
    }
  };

  const updatePreview = (field, value) => {
    setPreview(prev => ({ ...prev, [field]: value }));
  };

  const addTag = (tag) => {
    if (!tag.trim()) return;
    const normalizedTag = tag.trim().toLowerCase();
    if (preview.tags?.includes(normalizedTag)) {
      showToast('Тег уже существует', 'info');
      return;
    }
    updatePreview('tags', [...(preview.tags || []), normalizedTag]);
  };

  const removeTag = (index) => {
    updatePreview('tags', preview.tags.filter((_, i) => i !== index));
  };

  const addChecklistItem = (item) => {
    if (!item.trim()) return;
    updatePreview('checklist', [...(preview.checklist || []), item.trim()]);
  };

  const removeChecklistItem = (index) => {
    updatePreview('checklist', preview.checklist.filter((_, i) => i !== index));
  };

  const editChecklistItem = (index, value) => {
    const newChecklist = [...preview.checklist];
    newChecklist[index] = value;
    updatePreview('checklist', newChecklist);
  };

  const addContact = (contact) => {
    if (!contact.name.trim()) return;
    const newContact = {
      name: contact.name.trim(),
      phone: contact.phone?.trim() || '',
      email: contact.email?.trim() || ''
    };
    updatePreview('contacts', [...(preview.contacts || []), newContact]);
  };

  const removeContact = (index) => {
    updatePreview('contacts', preview.contacts.filter((_, i) => i !== index));
  };

  const editContact = (index, field, value) => {
    const newContacts = [...preview.contacts];
    newContacts[index] = { ...newContacts[index], [field]: value };
    updatePreview('contacts', newContacts);
  };

  const addPlace = (place) => {
    if (!place.name.trim()) return;
    const newPlace = {
      name: place.name.trim(),
      address: place.address?.trim() || ''
    };
    updatePreview('places', [...(preview.places || []), newPlace]);
  };

  const removePlace = (index) => {
    updatePreview('places', preview.places.filter((_, i) => i !== index));
  };

  const editPlace = (index, field, value) => {
    const newPlaces = [...preview.places];
    newPlaces[index] = { ...newPlaces[index], [field]: value };
    updatePreview('places', newPlaces);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-fuchsia-50">

      <div className="p-4 space-y-4">
        {/* Input - show only if no preview */}
        {!preview && (
          <div className="relative group">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-violet-600 to-fuchsia-600 rounded-2xl opacity-20 group-hover:opacity-30 blur transition"></div>
            <div className="relative bg-white rounded-2xl shadow-xl p-5">
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Опишите задачу... ✍️&#10;Например: &quot;Купить молоко завтра в 18:00&quot;"
                className="w-full min-h-32 text-lg resize-none focus:outline-none placeholder-gray-400 text-gray-800"
                autoFocus
              />
            </div>
          </div>
        )}

        {/* Edit prompt button - show when preview exists - YouTube Style */}
        {preview && (
          <button
            onClick={() => setPreview(null)}
            className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-gray-100 to-gray-200 rounded-xl shadow-lg hover:shadow-xl text-gray-700 font-semibold active:scale-95 transition-all duration-200 border border-gray-300 hover:from-gray-200 hover:to-gray-300"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
            </svg>
            Изменить промт
          </button>
        )}

        {/* Preview */}
        {preview && (
          <PreviewCard
            preview={preview}
            updatePreview={updatePreview}
            addTag={addTag}
            removeTag={removeTag}
            addChecklistItem={addChecklistItem}
            removeChecklistItem={removeChecklistItem}
            editChecklistItem={editChecklistItem}
            addContact={addContact}
            removeContact={removeContact}
            editContact={editContact}
            addPlace={addPlace}
            removePlace={removePlace}
            editPlace={editPlace}
          />
        )}

        {/* Buttons - YouTube Style */}
        {!preview && (
          <div className="space-y-3">
            <button
              onClick={handleAnalyze}
              disabled={!text.trim() || loading}
              className="w-full bg-gradient-to-r from-violet-500 to-purple-500 text-white rounded-xl py-4 px-6 font-bold shadow-lg hover:shadow-xl hover:from-violet-600 hover:to-purple-600 active:scale-95 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Анализирую...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                  </svg>
                  Разобрать задачу
                </span>
              )}
            </button>
            
            <button
              onClick={handleVoiceRecord}
              disabled={loading}
              className="w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl py-4 px-6 font-bold shadow-lg hover:shadow-xl hover:from-green-600 hover:to-emerald-600 active:scale-95 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Записываю...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"/>
                  </svg>
                  Записать голос
                </span>
              )}
            </button>
            
            {/* Press and Hold Microphone Button */}
            <button
              onMouseDown={startRecording}
              onMouseUp={stopRecording}
              onMouseLeave={stopRecording}
              onTouchStart={startRecording}
              onTouchEnd={stopRecording}
              disabled={loading || isRecording}
              className={`w-full rounded-xl py-4 px-6 font-bold shadow-lg transition-all duration-200 flex items-center justify-center gap-2 ${
                isRecording 
                  ? 'bg-gradient-to-r from-red-500 to-pink-500 text-white animate-pulse' 
                  : 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white hover:from-blue-600 hover:to-cyan-600 active:scale-95'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {isRecording ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 bg-white rounded-full animate-pulse"></div>
                  Записываю... Отпустите
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"/>
                  </svg>
                  🎤 Держите для записи
                </span>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function PreviewCard({ preview, updatePreview, addTag, removeTag, addChecklistItem, removeChecklistItem, editChecklistItem, addContact, removeContact, editContact, addPlace, removePlace, editPlace }) {
  const [newTag, setNewTag] = useState('');
  const [newItem, setNewItem] = useState('');
  const [newContact, setNewContact] = useState({ name: '', phone: '', email: '' });
  const [newPlace, setNewPlace] = useState({ name: '', address: '' });

  const handleAddTag = () => {
    addTag(newTag);
    setNewTag('');
  };

  const handleAddItem = () => {
    addChecklistItem(newItem);
    setNewItem('');
  };

  const handleAddContact = () => {
    addContact(newContact);
    setNewContact({ name: '', phone: '', email: '' });
  };

  const handleAddPlace = () => {
    addPlace(newPlace);
    setNewPlace({ name: '', address: '' });
  };

  return (
    <div className="relative group">
      <div className="absolute -inset-0.5 bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 rounded-2xl opacity-20 blur"></div>
      <div className="relative bg-white/90 backdrop-blur-md rounded-2xl shadow-2xl p-5 space-y-4 border border-white/50">
        
        {/* Header */}
        <div className="flex items-center justify-between">
          <span className="text-sm font-bold bg-gradient-to-r from-violet-600 to-fuchsia-600 bg-clip-text text-transparent">
            📋 Предпросмотр
          </span>
          {preview.model && (
            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gradient-to-r from-violet-500/20 to-purple-500/20 text-violet-700 border border-violet-200">
              ✨ {preview.model}
            </span>
          )}
        </div>

        {/* Task Name */}
        <input
          type="text"
          value={preview.taskName || ''}
          onChange={(e) => updatePreview('taskName', e.target.value)}
          className="w-full text-xl font-bold text-gray-900 bg-gradient-to-r from-violet-50/50 to-purple-50/50 border-2 border-transparent hover:border-violet-200 focus:border-violet-500 focus:outline-none rounded-xl px-4 py-3 transition-all"
          placeholder="Название задачи"
        />

        {/* Date & Time */}
        {preview.dateTime && (
          <div className="space-y-2 pt-2">
            <div className="flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-50/50 to-cyan-50/50 rounded-xl border border-blue-200">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
              </svg>
              <span className="text-sm font-semibold text-blue-900">
                📅 {preview.dateTime}
              </span>
            </div>
          </div>
        )}

        {/* RRule (Recurrence) */}
        {preview.rrule && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-green-50/50 to-emerald-50/50 rounded-xl border border-green-200">
              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
              </svg>
              <span className="text-xs font-semibold text-green-900">
                🔁 {preview.rrule}
              </span>
            </div>
          </div>
        )}

        {/* Priority */}
        {preview.priority && (
          <div className="space-y-2">
            <div className={`flex items-center gap-2 px-4 py-3 rounded-xl border ${
              preview.priority === 'high' 
                ? 'bg-gradient-to-r from-red-50/50 to-orange-50/50 border-red-200' 
                : preview.priority === 'medium'
                ? 'bg-gradient-to-r from-yellow-50/50 to-amber-50/50 border-yellow-200'
                : 'bg-gradient-to-r from-blue-50/50 to-cyan-50/50 border-blue-200'
            }`}>
              <span className="text-sm font-semibold">
                {preview.priority === 'high' ? '🔴 Высокий приоритет' : preview.priority === 'medium' ? '🟡 Средний приоритет' : '🔵 Низкий приоритет'}
              </span>
            </div>
          </div>
        )}

        {/* Tags */}
        <div className="space-y-3 pt-4 border-t border-violet-100/50">
          <span className="text-sm font-bold text-violet-700">🏷️ Теги</span>
          <div className="flex flex-wrap gap-2">
            {preview.tags?.length > 0 ? (
              preview.tags.map((tag, idx) => (
                <span key={idx} className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-violet-100 to-purple-100 text-violet-800 rounded-xl text-sm font-semibold border border-violet-200 shadow-sm">
                  #{tag}
                  <button
                    onClick={() => removeTag(idx)}
                    className="ml-2 text-violet-600 hover:text-violet-900 active:scale-90 transition-all"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                </span>
              ))
            ) : (
              <span className="text-sm text-gray-400 italic">Нет тегов</span>
            )}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
              placeholder="🏷️ Введите тег..."
              className="flex-1 px-4 py-3 text-sm border-2 border-violet-300 rounded-xl focus:outline-none focus:border-violet-500 bg-violet-50/50 transition-all"
            />
            <button
              onClick={handleAddTag}
              disabled={!newTag.trim()}
              className="px-6 py-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl font-bold hover:from-violet-700 hover:to-purple-700 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              +
            </button>
          </div>
        </div>

        {/* Contacts */}
        <div className="space-y-3 pt-4 border-t border-blue-100/50">
          <span className="text-sm font-bold text-blue-700">👥 Контакты</span>
          <div className="space-y-2">
            {preview.contacts?.length > 0 ? (
              preview.contacts.map((contact, idx) => (
                <div key={idx} className="group flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-blue-50/50 to-cyan-50/50 border border-blue-100 hover:border-blue-300 hover:shadow-md transition-all">
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-2">
                    <input
                      type="text"
                      value={contact.name}
                      onChange={(e) => editContact(idx, 'name', e.target.value)}
                      placeholder="Имя"
                      className="px-3 py-2 text-sm border border-blue-200 rounded-lg focus:outline-none focus:border-blue-500 bg-white"
                    />
                    <input
                      type="tel"
                      value={contact.phone}
                      onChange={(e) => editContact(idx, 'phone', e.target.value)}
                      placeholder="Телефон"
                      className="px-3 py-2 text-sm border border-blue-200 rounded-lg focus:outline-none focus:border-blue-500 bg-white"
                    />
                    <input
                      type="email"
                      value={contact.email}
                      onChange={(e) => editContact(idx, 'email', e.target.value)}
                      placeholder="Email"
                      className="px-3 py-2 text-sm border border-blue-200 rounded-lg focus:outline-none focus:border-blue-500 bg-white"
                    />
                  </div>
                  <button
                    onClick={() => removeContact(idx)}
                    className="opacity-60 group-hover:opacity-100 text-red-500 hover:text-red-700 hover:bg-red-50 active:scale-90 transition-all p-1.5 rounded-lg"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                    </svg>
                  </button>
                </div>
              ))
            ) : (
              <span className="text-sm text-gray-400 italic">Нет контактов</span>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <input
              type="text"
              value={newContact.name}
              onChange={(e) => setNewContact({...newContact, name: e.target.value})}
              placeholder="Имя"
              className="px-3 py-2 text-sm border border-blue-300 rounded-lg focus:outline-none focus:border-blue-500 bg-blue-50/50"
            />
            <input
              type="tel"
              value={newContact.phone}
              onChange={(e) => setNewContact({...newContact, phone: e.target.value})}
              placeholder="Телефон"
              className="px-3 py-2 text-sm border border-blue-300 rounded-lg focus:outline-none focus:border-blue-500 bg-blue-50/50"
            />
            <div className="flex gap-2">
              <input
                type="email"
                value={newContact.email}
                onChange={(e) => setNewContact({...newContact, email: e.target.value})}
                placeholder="Email"
                className="flex-1 px-3 py-2 text-sm border border-blue-300 rounded-lg focus:outline-none focus:border-blue-500 bg-blue-50/50"
              />
              <button
                onClick={handleAddContact}
                disabled={!newContact.name.trim()}
                className="px-4 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg font-bold hover:from-blue-700 hover:to-cyan-700 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                +
              </button>
            </div>
          </div>
        </div>

        {/* Places */}
        <div className="space-y-3 pt-4 border-t border-green-100/50">
          <span className="text-sm font-bold text-green-700">📍 Места</span>
          <div className="space-y-2">
            {preview.places?.length > 0 ? (
              preview.places.map((place, idx) => (
                <div key={idx} className="group flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-green-50/50 to-emerald-50/50 border border-green-100 hover:border-green-300 hover:shadow-md transition-all">
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2">
                    <input
                      type="text"
                      value={place.name}
                      onChange={(e) => editPlace(idx, 'name', e.target.value)}
                      placeholder="Название места"
                      className="px-3 py-2 text-sm border border-green-200 rounded-lg focus:outline-none focus:border-green-500 bg-white"
                    />
                    <input
                      type="text"
                      value={place.address}
                      onChange={(e) => editPlace(idx, 'address', e.target.value)}
                      placeholder="Адрес"
                      className="px-3 py-2 text-sm border border-green-200 rounded-lg focus:outline-none focus:border-green-500 bg-white"
                    />
                  </div>
                  <button
                    onClick={() => removePlace(idx)}
                    className="opacity-60 group-hover:opacity-100 text-red-500 hover:text-red-700 hover:bg-red-50 active:scale-90 transition-all p-1.5 rounded-lg"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                    </svg>
                  </button>
                </div>
              ))
            ) : (
              <span className="text-sm text-gray-400 italic">Нет мест</span>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <input
              type="text"
              value={newPlace.name}
              onChange={(e) => setNewPlace({...newPlace, name: e.target.value})}
              placeholder="Название места"
              className="px-3 py-2 text-sm border border-green-300 rounded-lg focus:outline-none focus:border-green-500 bg-green-50/50"
            />
            <div className="flex gap-2">
              <input
                type="text"
                value={newPlace.address}
                onChange={(e) => setNewPlace({...newPlace, address: e.target.value})}
                placeholder="Адрес"
                className="flex-1 px-3 py-2 text-sm border border-green-300 rounded-lg focus:outline-none focus:border-green-500 bg-green-50/50"
              />
              <button
                onClick={handleAddPlace}
                disabled={!newPlace.name.trim()}
                className="px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg font-bold hover:from-green-700 hover:to-emerald-700 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                +
              </button>
            </div>
          </div>
        </div>

        {/* Checklist */}
        <div className="space-y-3 pt-4 border-t border-purple-100/50">
          <span className="text-sm font-bold text-purple-700">✅ Чек-лист</span>
          <div className="space-y-2">
            {preview.checklist?.length > 0 ? (
              preview.checklist.map((item, idx) => (
                <div key={idx} className="group flex items-start gap-3 p-3 rounded-xl bg-gradient-to-r from-purple-50/50 to-fuchsia-50/50 border border-purple-100 hover:border-purple-300 hover:shadow-md transition-all">
                  <div className="flex-shrink-0 mt-1">
                    <div className="w-5 h-5 rounded-full border-2 border-purple-400 bg-white flex items-center justify-center">
                      <svg className="w-3 h-3 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"/>
                      </svg>
                    </div>
                  </div>
                  <input
                    type="text"
                    value={item}
                    onChange={(e) => editChecklistItem(idx, e.target.value)}
                    className="flex-1 text-sm font-medium text-gray-900 bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-purple-400 rounded-lg px-2 py-1 -ml-2"
                  />
                  <button
                    onClick={() => removeChecklistItem(idx)}
                    className="opacity-60 group-hover:opacity-100 text-red-500 hover:text-red-700 hover:bg-red-50 active:scale-90 transition-all p-1.5 rounded-lg"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                    </svg>
                  </button>
                </div>
              ))
            ) : (
              <span className="text-sm text-gray-400 italic">Нет пунктов</span>
            )}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={newItem}
              onChange={(e) => setNewItem(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddItem()}
              placeholder="✍️ Введите пункт..."
              className="flex-1 px-4 py-3 text-sm border-2 border-purple-300 rounded-xl focus:outline-none focus:border-purple-500 bg-purple-50/50 transition-all"
            />
            <button
              onClick={handleAddItem}
              disabled={!newItem.trim()}
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white rounded-xl font-bold hover:from-purple-700 hover:to-fuchsia-700 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              +
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreateTask;

